import React, { useEffect, useMemo, useState } from 'react';
import { MapContainer, TileLayer, Marker } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { FaTimes, FaPhoneAlt, FaMapMarkerAlt, FaCheck, FaBan, FaTrash, FaEdit, FaUndo, FaEye, FaStore } from 'react-icons/fa';
import MediaLightbox from './MediaLightbox';
import ShopRatingsSection from './ShopRatingsSection';
import { DAY_NAMES, StatusChip, formatDate, hoursText, markerIcon } from './shopConstants';

const RESOLUTION_OPTIONS = [
    { value: 'accept', label: 'قبول كتخصص جديد' },
    { value: 'rename', label: 'قبول باسم آخر' },
    { value: 'map', label: 'ربط بتخصص موجود' },
    { value: 'drop', label: 'تجاهل' },
];

const idsOf = (list = []) => list.map((s) => String(s?._id || s));

const SOCIAL_LABELS = { facebook: 'فيسبوك', instagram: 'إنستغرام', tiktok: 'تيك توك', snapchat: 'سناب شات', website: 'الموقع' };
const SOCIAL_BASE = {
    facebook: (h) => `https://www.facebook.com/${h}`,
    instagram: (h) => `https://www.instagram.com/${h}`,
    tiktok: (h) => `https://www.tiktok.com/@${h}`,
    snapchat: (h) => `https://www.snapchat.com/add/${h}`,
    website: (h) => `https://${h}`,
};
const socialHref = (key, value) => (/^https?:\/\//i.test(value) ? value : SOCIAL_BASE[key]?.(String(value).replace(/^@/, '')));
const filledSocials = (socials) => Object.fromEntries(Object.entries(socials || {}).filter(([, v]) => v));

// Review / view / edit one shop. For an approved shop with pending edits the review shows the
// proposed version, marking every field that differs from what's live now.
const ShopDetailsModal = ({ shopId, actions, specialities, cities, onClose, onChanged }) => {
    const [shop, setShop] = useState(null);
    const [loading, setLoading] = useState(true);
    const [lightbox, setLightbox] = useState(null);
    const [resolutions, setResolutions] = useState({});
    const [addCity, setAddCity] = useState(true);
    const [reasonMode, setReasonMode] = useState(null); // 'reject' | 'suspend' | 'reject-update'
    const [reason, setReason] = useState('');
    const [editing, setEditing] = useState(false);
    const [form, setForm] = useState(null);
    const [confirmDelete, setConfirmDelete] = useState(false);

    const load = async () => {
        setLoading(true);
        const json = await actions.getShop(shopId);
        setShop(json);
        setLoading(false);
    };

    useEffect(() => { load(); }, [shopId]); // eslint-disable-line react-hooks/exhaustive-deps

    const isUpdate = Boolean(shop?.status === 'approved' && shop?.pendingUpdate);
    const isNew = shop?.status === 'pending' || shop?.status === 'rejected';
    const content = isUpdate ? shop.pendingUpdate : shop;
    const proposed = content?.proposedSpecialities || [];

    const changed = useMemo(() => {
        if (!isUpdate) return {};
        const live = shop;
        const next = shop.pendingUpdate;
        const same = (a, b) => JSON.stringify(a ?? '') === JSON.stringify(b ?? '');
        return {
            name: !same(live.name, next.name),
            phone: !same(live.phone, next.phone),
            city: !same(live.city, next.city),
            address: !same(live.address, next.address),
            logo: !same(live.logo, next.logo),
            video: !same(live.video, next.video),
            images: !same(live.images, next.images),
            location: !same(live.location, next.location),
            description: !same(live.description, next.description),
            socials: !same(filledSocials(live.socials), filledSocials(next.socials)),
            specialities: !same(idsOf(live.specialities).sort(), idsOf(next.specialities).sort()) || (next.proposedSpecialities || []).length > 0,
        };
    }, [isUpdate, shop]);

    const setResolution = (name, patch) => setResolutions((prev) => ({ ...prev, [name]: { action: 'accept', ...prev[name], ...patch } }));

    const buildResolutions = () => proposed.map((name) => ({ proposed: name, ...(resolutions[name] || { action: 'accept' }) }));

    const resolutionsValid = proposed.every((name) => {
        const r = resolutions[name] || { action: 'accept' };
        if (r.action === 'rename') return Boolean(r.newName && r.newName.trim());
        if (r.action === 'map') return Boolean(r.specialityId);
        return true;
    });

    const approve = async () => {
        const body = { specialityResolutions: buildResolutions(), addCity: !content.cityExists && addCity };
        const ok = isUpdate ? await actions.approvePendingUpdate(shop._id, body) : await actions.approveShop(shop._id, body);
        if (ok) { onChanged(); onClose(); }
    };

    const submitReason = async () => {
        if (!reason.trim()) return;
        let ok = null;
        if (reasonMode === 'reject') ok = await actions.rejectShop(shop._id, reason.trim());
        if (reasonMode === 'reject-update') ok = await actions.rejectPendingUpdate(shop._id, reason.trim());
        if (reasonMode === 'suspend') ok = await actions.suspendShop(shop._id, reason.trim());
        if (ok) { onChanged(); onClose(); }
    };

    const reinstate = async () => {
        if (await actions.reinstateShop(shop._id)) { onChanged(); load(); }
    };

    const remove = async () => {
        if (await actions.deleteShop(shop._id)) { onChanged(); onClose(); }
    };

    const startEdit = () => {
        setForm({
            name: shop.name,
            phone: shop.phone,
            city: shop.city,
            address: shop.address,
            specialities: idsOf(shop.specialities),
            description: shop.description || '',
            socials: { ...filledSocials(shop.socials) },
        });
        setEditing(true);
    };

    const saveEdit = async (e) => {
        e.preventDefault();
        const body = {
            ...form,
            location: shop.location,
            logo: shop.logo,
            video: shop.video,
            images: shop.images,
            openingHours: shop.openingHours,
            proposedSpecialities: [],
        };
        if (await actions.updateShop(shop._id, body)) {
            setEditing(false);
            onChanged();
            load();
        }
    };

    const toggleFormSpeciality = (id) => setForm((prev) => ({
        ...prev,
        specialities: prev.specialities.includes(id) ? prev.specialities.filter((s) => s !== id) : [...prev.specialities, id],
    }));

    const gallery = content ? [content.logo, ...(content.images || [])].filter(Boolean) : [];
    const Changed = ({ field }) => (changed[field] ? <span className="changed-badge">تغيّر</span> : null);

    return (
        <div className="shops-modal-overlay" onClick={onClose}>
            <div className="shops-modal wide" onClick={(e) => e.stopPropagation()}>
                <button type="button" className="modal-close" onClick={onClose} aria-label="إغلاق"><FaTimes /></button>

                {loading || !shop ? (
                    <div className="modal-loading">جاري التحميل...</div>
                ) : (
                    <>
                        <div className="shop-hero">
                            {content.logo ? (
                                <img className="shop-logo" src={content.logo} alt="" onClick={() => setLightbox(0)} />
                            ) : (
                                <div className="shop-logo placeholder"><FaStore /></div>
                            )}
                            <div className="shop-hero-text">
                                <div className="shop-hero-title">
                                    <h2>{content.name}</h2>
                                    <Changed field="name" />
                                    <StatusChip status={shop.status} />
                                    {isUpdate && <span className="status-chip tone-info">تعديلات بانتظار المراجعة</span>}
                                </div>
                                <div className="shop-hero-meta">
                                    <span><FaPhoneAlt /> <bdi>{content.phone}</bdi><Changed field="phone" /></span>
                                    <span>
                                        <FaMapMarkerAlt /> {content.city}
                                        <span className={`flag ${content.cityExists ? 'flag-ok' : 'flag-missing'}`}>{content.cityExists ? 'مدينة موجودة' : 'مدينة جديدة'}</span>
                                        <Changed field="city" />
                                    </span>
                                    <span>المالك: {shop.ownerId?.ownerName || '—'} · <bdi>{shop.ownerId?.phoneNumber}</bdi></span>
                                </div>
                                {shop.rejectionReason && shop.status !== 'approved' && (
                                    <div className="reason-note">سبب {shop.status === 'suspended' ? 'الإيقاف' : 'الرفض'}: {shop.rejectionReason}</div>
                                )}
                            </div>
                        </div>

                        {editing ? (
                            <form className="shop-edit-form" onSubmit={saveEdit}>
                                <div className="form-grid">
                                    <label>اسم الورشة<input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required /></label>
                                    <label>رقم الهاتف<input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} required dir="ltr" /></label>
                                    <label>المدينة
                                        <input list="shop-cities" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} required />
                                        <datalist id="shop-cities">{cities.map((c) => <option key={c} value={c} />)}</datalist>
                                    </label>
                                    <label>العنوان<input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} required /></label>
                                </div>
                                <label className="full-width-label">وصف الورشة
                                    <textarea rows={3} maxLength={1000} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
                                </label>
                                <div className="form-grid">
                                    {Object.keys(SOCIAL_LABELS).map((key) => (
                                        <label key={key}>{SOCIAL_LABELS[key]}
                                            <input dir="ltr" value={form.socials[key] || ''} placeholder="اسم الحساب أو الرابط" onChange={(e) => setForm({ ...form, socials: { ...form.socials, [key]: e.target.value } })} />
                                        </label>
                                    ))}
                                </div>
                                <div className="field-label">التخصصات</div>
                                <div className="chip-picker">
                                    {specialities.map((s) => (
                                        <button type="button" key={s._id} className={form.specialities.includes(String(s._id)) ? 'active' : ''} onClick={() => toggleFormSpeciality(String(s._id))}>
                                            {s.name}
                                        </button>
                                    ))}
                                </div>
                                <div className="modal-actions">
                                    <button type="submit" className="btn primary" disabled={actions.busy || form.specialities.length === 0}><FaCheck /> حفظ</button>
                                    <button type="button" className="btn ghost" onClick={() => setEditing(false)}>إلغاء</button>
                                </div>
                            </form>
                        ) : (
                            <>
                                <div className="detail-grid">
                                    <section className="detail-card">
                                        <h3>التخصصات <Changed field="specialities" /></h3>
                                        <div className="chips">
                                            {(content.specialities || []).map((s) => <span key={s._id || s} className="chip">{s.name || s}</span>)}
                                            {proposed.map((name) => <span key={name} className="chip chip-new">{name} <em>جديد</em></span>)}
                                        </div>

                                        {(isNew || isUpdate) && proposed.length > 0 && (
                                            <div className="resolutions">
                                                <div className="hint">تخصصات مقترحة من صاحب الورشة — اختر ما يحدث لكل منها عند الاعتماد:</div>
                                                {proposed.map((name) => {
                                                    const r = resolutions[name] || { action: 'accept' };
                                                    return (
                                                        <div className="resolution-row" key={name}>
                                                            <strong>{name}</strong>
                                                            <select value={r.action} onChange={(e) => setResolution(name, { action: e.target.value })}>
                                                                {RESOLUTION_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                                                            </select>
                                                            {r.action === 'rename' && (
                                                                <input placeholder="الاسم الصحيح" value={r.newName || ''} onChange={(e) => setResolution(name, { newName: e.target.value })} />
                                                            )}
                                                            {r.action === 'map' && (
                                                                <select value={r.specialityId || ''} onChange={(e) => setResolution(name, { specialityId: e.target.value })}>
                                                                    <option value="">اختر تخصصاً</option>
                                                                    {specialities.map((s) => <option key={s._id} value={s._id}>{s.name}</option>)}
                                                                </select>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}

                                        <h3>العنوان <Changed field="address" /></h3>
                                        <p className="address">{content.address}</p>

                                        <h3>أوقات العمل</h3>
                                        <table className="hours-table">
                                            <tbody>
                                                {DAY_NAMES.map((day, i) => {
                                                    const entry = (shop.openingHours || []).find((h) => h.day === i);
                                                    return (
                                                        <tr key={day} className={entry?.isClosed ? 'closed' : ''}>
                                                            <td>{day}</td>
                                                            <td dir="ltr">{hoursText(entry)}</td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </section>

                                    <section className="detail-card">
                                        <h3>الموقع <Changed field="location" /></h3>
                                        {content.location && (
                                            <div className="map-box">
                                                <MapContainer
                                                    key={`${content.location.latitude},${content.location.longitude}`}
                                                    center={[content.location.latitude, content.location.longitude]}
                                                    zoom={15}
                                                    style={{ height: '220px', width: '100%' }}
                                                >
                                                    <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="&copy; OpenStreetMap" />
                                                    <Marker position={[content.location.latitude, content.location.longitude]} icon={markerIcon} />
                                                </MapContainer>
                                            </div>
                                        )}
                                        <a className="map-link" target="_blank" rel="noreferrer" href={`https://www.google.com/maps?q=${content.location?.latitude},${content.location?.longitude}`}>فتح في خرائط Google</a>

                                        <div className="stats-row">
                                            <div><strong>{shop.stats?.views || 0}</strong><span>مشاهدة</span></div>
                                            <div><strong>{shop.stats?.calls || 0}</strong><span>اتصال</span></div>
                                            <div><strong>{shop.stats?.directions || 0}</strong><span>اتجاهات</span></div>
                                        </div>
                                        <div className="muted small">أُضيفت: {formatDate(shop.createdAt)}{shop.approvedAt ? ` · اعتمدت: ${formatDate(shop.approvedAt)}` : ''}</div>
                                    </section>
                                </div>

                                {content.description || Object.keys(filledSocials(content.socials)).length ? (
                                    <section className="detail-card">
                                        <h3>عن الورشة <Changed field="description" /></h3>
                                        {content.description ? <p className="shop-description">{content.description}</p> : <p className="muted">بدون وصف</p>}
                                        <h3>وسائل التواصل <Changed field="socials" /></h3>
                                        <div className="chips">
                                            {Object.entries(filledSocials(content.socials)).map(([key, value]) => (
                                                <a key={key} className="chip" href={socialHref(key, value)} target="_blank" rel="noreferrer">
                                                    {SOCIAL_LABELS[key] || key}: <bdi>{value}</bdi>
                                                </a>
                                            ))}
                                            {Object.keys(filledSocials(content.socials)).length === 0 && <span className="muted">لا يوجد</span>}
                                        </div>
                                    </section>
                                ) : null}

                                {shop.status === 'approved' && <ShopRatingsSection shopId={shop._id} actions={actions} />}

                                <section className="detail-card">
                                    <h3>الصور <Changed field="images" /> <Changed field="logo" /></h3>
                                    <div className="gallery">
                                        {gallery.map((src, i) => (
                                            <button type="button" key={src + i} className="gallery-item" onClick={() => setLightbox(i)}>
                                                <img src={src} alt="" />
                                                {i === 0 && <span className="gallery-tag">الشعار</span>}
                                            </button>
                                        ))}
                                    </div>
                                    {content.video && (
                                        <>
                                            <h3>الفيديو <Changed field="video" /></h3>
                                            <video className="shop-video" src={content.video} controls preload="metadata" />
                                        </>
                                    )}
                                </section>

                                {reasonMode ? (
                                    <div className="reason-box">
                                        <label>{reasonMode === 'suspend' ? 'سبب الإيقاف' : 'سبب الرفض'} (يصل لصاحب الورشة)</label>
                                        <textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} autoFocus />
                                        <div className="modal-actions">
                                            <button type="button" className="btn danger" disabled={!reason.trim() || actions.busy} onClick={submitReason}>تأكيد</button>
                                            <button type="button" className="btn ghost" onClick={() => { setReasonMode(null); setReason(''); }}>إلغاء</button>
                                        </div>
                                    </div>
                                ) : confirmDelete ? (
                                    <div className="reason-box">
                                        <label>حذف الورشة نهائياً؟ سيتم إيقاف إعلاناتها وإرجاع المبالغ غير المستخدمة لصاحبها.</label>
                                        <div className="modal-actions">
                                            <button type="button" className="btn danger" disabled={actions.busy} onClick={remove}><FaTrash /> حذف نهائي</button>
                                            <button type="button" className="btn ghost" onClick={() => setConfirmDelete(false)}>إلغاء</button>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="modal-actions">
                                        {(isNew || isUpdate) && (
                                            <>
                                                {!content.cityExists && (
                                                    <label className="inline-check">
                                                        <input type="checkbox" checked={addCity} onChange={(e) => setAddCity(e.target.checked)} />
                                                        إضافة "{content.city}" إلى قائمة المدن
                                                    </label>
                                                )}
                                                <button type="button" className="btn success" disabled={actions.busy || !resolutionsValid} onClick={approve}>
                                                    <FaCheck /> {isUpdate ? 'اعتماد التعديلات' : 'اعتماد الورشة'}
                                                </button>
                                                {shop.status !== 'rejected' && (
                                                    <button type="button" className="btn danger-outline" onClick={() => setReasonMode(isUpdate ? 'reject-update' : 'reject')}>
                                                        <FaBan /> {isUpdate ? 'رفض التعديلات' : 'رفض'}
                                                    </button>
                                                )}
                                            </>
                                        )}
                                        {shop.status === 'approved' && (
                                            <button type="button" className="btn warning-outline" onClick={() => setReasonMode('suspend')}><FaEye /> إيقاف الظهور</button>
                                        )}
                                        {shop.status === 'suspended' && (
                                            <button type="button" className="btn success" onClick={reinstate}><FaUndo /> إعادة التفعيل</button>
                                        )}
                                        {!isUpdate && <button type="button" className="btn ghost" onClick={startEdit}><FaEdit /> تعديل</button>}
                                        <button type="button" className="btn ghost danger-text" onClick={() => setConfirmDelete(true)}><FaTrash /> حذف</button>
                                    </div>
                                )}
                            </>
                        )}
                    </>
                )}
            </div>
            {lightbox !== null && (
                <MediaLightbox images={gallery} index={lightbox} onChange={setLightbox} onClose={() => setLightbox(null)} />
            )}
        </div>
    );
};

export default ShopDetailsModal;
